import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { GameCard, GameCardSkeleton } from '../components/GameCard';
import HeroVisual from '../components/HeroVisual';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Zap, Shield, Tag, Headphones, Activity, Cpu, Radio } from 'lucide-react';
import { toast } from 'sonner';
import theme from '../theme.config';
const HERO_BG = theme.assets.heroBg;
const COPY = theme.copy;

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', name: 'Semua' },
  { id: 'games', name: 'Games' },
  { id: 'pulsa', name: 'Pulsa' },
  { id: 'voucher', name: 'Voucher' },
];

const TELEMETRY_LOGS = [
  '[STEAM_OK] Boiler turbine 04 engaged - 500 Diamonds',
  '[VALVE_OK] Pressure conduit aligned with DigiFlazz',
  '[CARGO_OK] 86 UC delivered to sector 07',
  '[STEAM_OK] Dispatch valve opened for 50K payload',
  '[BOILER]   Reactor latency calibration: 12ms ✓',
  '[CARGO_OK] Valorant 1650 VP loaded to ID 81920843',
  '[STEAM_OK] Call of Duty 400 CP pressure confirmed',
  '[VALVE_OK] Reseller node synced — status: ENGINEER',
  '[CARGO_OK] Genshin Impact 980 Genesis core calibrated',
  '[BOILER]   Aether current TXN token 0x9AF2 generated',
  '[VALVE_OK] Security brass shield verified',
  '[CARGO_OK] Transmitted 25K Pulsa via pressure valve',
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [digiflazzBrands, setDigiflazzBrands] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tick, setTick] = useState(0);

  const [boilerPressure, setBoilerPressure] = useState(14.2);
  const [boilerTemp, setBoilerTemp] = useState(240);
  const [boilerVenting, setBoilerVenting] = useState(false);
  const [valves, setValves] = useState([true, true, true, false, true, true]);

  useEffect(() => {
    const interval = setInterval(() => {
      setBoilerPressure(p => {
        if (boilerVenting) return p;
        const delta = (Math.random() - 0.5) * 0.4;
        const next = p + delta;
        return next < 12 ? 12.2 : next > 18 ? 17.8 : parseFloat(next.toFixed(1));
      });
      setBoilerTemp(t => {
        if (boilerVenting) return t;
        const delta = Math.round((Math.random() - 0.5) * 6);
        const next = t + delta;
        return next < 220 ? 222 : next > 260 ? 258 : next;
      });
    }, 2500);
    return () => clearInterval(interval);
  }, [boilerVenting]);

  const handleVentSteam = () => {
    if (boilerVenting) return;
    setBoilerVenting(true);
    setBoilerPressure(8.4);
    setBoilerTemp(180);
    setValves([false, false, false, true, true, false]);
    toast.success('VENTING VALVE ENGAGED: Depressurizing steam boiler chamber...', {
      icon: '💨',
      style: {
        background: '#1c1917',
        border: '1px solid #d97706',
        color: '#f59e0b',
        fontFamily: 'monospace'
      }
    });
    setTimeout(() => {
      setBoilerVenting(false);
      setBoilerPressure(14.0);
      setBoilerTemp(235);
      setValves([true, true, true, false, true, true]);
    }, 4000);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, digiflazzBrands, activeCategory, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live clock tick for HUD
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const fetchAll = async () => {
    try {
      const [seedRes, catalogRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/biller/catalog`),
      ]);

      if (seedRes.status === 'fulfilled') {
        setProducts(seedRes.value.data.products || []);
      }

      if (catalogRes.status === 'fulfilled' && catalogRes.value.data.success) {
        setDigiflazzBrands(catalogRes.value.data.brands || []);
      }

      try { await axios.post(`${API_URL}/api/seed`); } catch {}
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    const allItems = [];

    for (const brand of digiflazzBrands) {
      allItems.push({
        id: `df-${brand.slug}`,
        slug: `df/${brand.slug}`,
        name: brand.brand,
        image: brand.image,
        category: brand.category,
        source: 'digiflazz',
        itemCount: brand.items.length,
        startPrice: Math.min(...brand.items.map(i => i.price)),
      });
    }

    const dfNames = new Set(digiflazzBrands.map(b => b.brand.toLowerCase()));
    for (const p of products) {
      if (!dfNames.has(p.name.toLowerCase())) {
        const catMap = { game: 'games', voucher: 'voucher' };
        allItems.push({ ...p, category: catMap[p.category] || p.category, source: 'seed' });
      }
    }

    let filtered = [...allItems];

    if (activeCategory !== 'all') {
      filtered = filtered.filter(p => p.category === activeCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  };

  const now = new Date();
  const timeStr = now.toTimeString().split(' ')[0];
  const dateStr = now.toISOString().split('T')[0];

  return (
    <div className="min-h-screen pt-0">
      {/* ── HERO: 3-Column Cybernetic Dashboard ── */}
      <section className="relative overflow-hidden cyber-scanline">
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${HERO_BG}')` }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="cyber-grid absolute inset-0 opacity-60" />

        {/* Ambient glows */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] -translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px] translate-x-1/4 translate-y-1/4" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 md:pt-32 md:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">

            {/* Left: Command Console */}
            <div className="lg:col-span-7 flex flex-col justify-center">
              {/* HUD Date badge */}
              <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 w-fit">
                <Radio className="w-3 h-3 text-primary animate-pulse" />
                <span className="font-mono text-xs text-primary tracking-widest">REACTOR_ENGAGED · {dateStr}</span>
              </div>

              {/* Title */}
              <h1 className="font-bold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-2 leading-tight">
                {COPY.hero.titleLine1}
              </h1>
              <h2 className="font-bold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mb-6 leading-tight brand-gradient text-glow-green">
                {COPY.hero.titleLine2}
              </h2>

              <p className="text-foreground/70 text-sm md:text-base mb-8 max-w-xl leading-relaxed">
                {COPY.hero.subtitle}
              </p>

              {/* Search — HUD bracket style */}
              <div className="relative hud-bracket max-w-lg">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/70" />
                <Input
                  type="text"
                  placeholder={COPY.hero.searchPlaceholder}
                  className="pl-11 pr-4 py-6 bg-black/60 border-primary/30 text-white placeholder:text-muted-foreground rounded-lg focus:border-primary focus:ring-1 focus:ring-primary/40 font-mono text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-primary text-lg leading-none animate-blink">_</span>
              </div>

              {/* Quick stats */}
              <div className="flex items-center gap-6 mt-6">
                {[
                  { label: 'Products', val: loading ? '…' : filteredProducts.length.toString() },
                  { label: 'Instant', val: '< 3s' },
                  { label: 'Uptime', val: '99.9%' },
                ].map(s => (
                  <div key={s.label}>
                    <p className="font-mono text-xl font-bold text-primary text-glow-green">{s.val}</p>
                    <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Core Visualizer */}
            <div className="hidden lg:flex lg:col-span-5 justify-center items-center">
              <div className="cyber-card rounded-xl bg-card/60 backdrop-blur-sm p-6 w-full max-w-sm">
                {/* Header */}
                <div className="flex items-center gap-2 mb-4">
                  <Cpu className="w-4 h-4 text-secondary" />
                  <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">REACTOR_CORE</span>
                </div>
                <div className="flex justify-center">
                  <HeroVisual />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-1">
                  {['STEAM', 'VALVE', 'SYNC'].map((label, i) => (
                    <div key={label} className="text-center py-1 rounded bg-black/40 border border-border">
                      <div className={`w-1.5 h-1.5 rounded-full mx-auto mb-1 ${i === 0 ? 'bg-primary animate-pulse' : i === 1 ? 'bg-secondary animate-pulse' : 'bg-accent'}`} />
                      <p className="font-mono text-[9px] text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── FEATURES BAR ── */}
      <section className="py-10 border-b border-border bg-card/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {(() => {
              const featureIcons = [Zap, Tag, Shield, Headphones];
              const tones = [
                'border-primary/30 bg-primary/10 text-primary',
                'border-secondary/30 bg-secondary/10 text-secondary',
                'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
                'border-accent/30 bg-accent/10 text-accent',
              ];
              return COPY.features.map((f, idx) => {
                const Icon = featureIcons[idx % featureIcons.length];
                const tone = tones[idx % tones.length];
                return (
                  <div key={idx} className="flex items-center gap-3 group">
                    <div className={`w-12 h-12 rounded-xl border flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110 ${tone}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm">{f.label}</h3>
                      <p className="text-xs text-muted-foreground">{f.desc}</p>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* ── PRODUCTS GRID & TELEMETRY SECTION ── */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Products (Left) */}
            <div className="lg:col-span-9">
              {/* Category Tabs — cockpit-style buttons */}
              <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    className={`px-5 py-2 rounded-lg font-mono text-sm uppercase tracking-widest border transition-all duration-200 ${
                      activeCategory === cat.id
                        ? 'bg-primary/20 border-primary text-primary neon-green'
                        : 'bg-transparent border-border text-muted-foreground hover:border-primary/40 hover:text-primary/80'
                    }`}
                    onClick={() => setActiveCategory(cat.id)}
                    data-testid={`category-${cat.id}`}
                  >
                    {cat.name}
                  </button>
                ))}

                {!loading && (
                  <span className="ml-auto font-mono text-xs text-muted-foreground">
                    {filteredProducts.length} UNIT{filteredProducts.length !== 1 ? 'S' : ''} FOUND
                  </span>
                )}
              </div>

              {/* Grid */}
              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {[...Array(10)].map((_, i) => <GameCardSkeleton key={i} />)}
                </div>
              ) : filteredProducts.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {filteredProducts.map((product) => (
                    <GameCard key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="font-mono text-muted-foreground">[REACTOR_EMPTY] — No digital cargo chambers found</p>
                </div>
              )}
            </div>

            {/* Steampunk Boiler Console (Right) */}
            <div className="lg:col-span-3 lg:sticky lg:top-24">
              <div className="cyber-card rounded-xl bg-card/80 backdrop-blur-sm overflow-hidden relative border border-border">
                {/* Rivets decoration */}
                <div className="absolute top-1 left-1 w-1.5 h-1.5 rounded-full bg-primary/20 border border-primary/40" />
                <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary/20 border border-primary/40" />
                <div className="absolute bottom-1 left-1 w-1.5 h-1.5 rounded-full bg-primary/20 border border-primary/40" />
                <div className="absolute bottom-1 right-1 w-1.5 h-1.5 rounded-full bg-primary/20 border border-primary/40" />

                {/* Header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-black/30">
                  <Activity className="w-4 h-4 text-primary animate-pulse" />
                  <span className="font-mono text-xs text-primary tracking-widest uppercase font-bold">BOILER_CONDUIT_PANEL</span>
                  <span className="ml-auto font-mono text-[9px] text-muted-foreground animate-flicker">SYS_OK</span>
                </div>

                {/* Analog Gauge */}
                <div className="p-4 border-b border-border bg-black/10 flex flex-col items-center">
                  <span className="font-mono text-[9px] text-muted-foreground uppercase tracking-widest mb-2">PRESSURE GAUGE</span>
                  
                  <div className="relative w-32 h-20 mb-1">
                    <svg viewBox="0 0 100 55" className="w-full h-full">
                      {/* Arc backgrounds */}
                      <path d="M 10,50 A 40,40 0 0,1 90,50" fill="none" stroke="#2c2520" strokeWidth="6" strokeLinecap="round" />
                      {/* Normal zone (Yellow/Amber) */}
                      <path d="M 10,50 A 40,40 0 0,1 65,19" fill="none" stroke="#d97706" strokeWidth="6" strokeLinecap="round" opacity="0.8" />
                      {/* Danger zone (Red) */}
                      <path d="M 65,19 A 40,40 0 0,1 90,50" fill="none" stroke="#dc2626" strokeWidth="6" strokeLinecap="round" opacity="0.8" />
                      
                      {/* Needle */}
                      <line 
                        x1="50" y1="50" x2="50" y2="15" 
                        stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" 
                        style={{ 
                          transform: `rotate(${(boilerPressure - 10) * 18 - 90}deg)`, 
                          transformOrigin: '50px 50px',
                          transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                        }} 
                      />
                      {/* Needle Center Pin */}
                      <circle cx="50" cy="50" r="5" fill="#78350f" stroke="#f59e0b" strokeWidth="1" />
                    </svg>
                    
                    {/* Floating pressure digits */}
                    <div className="absolute bottom-1 left-0 right-0 text-center">
                      <span className="font-mono text-sm font-bold text-primary text-glow-green">
                        {boilerPressure.toFixed(1)} Bar
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-between w-full font-mono text-[9px] text-muted-foreground px-2">
                    <span>10 BAR</span>
                    <span>15 BAR</span>
                    <span>20 BAR</span>
                  </div>
                </div>

                {/* Valve Matrix & Temperature */}
                <div className="p-4 space-y-4">
                  {/* Temp & Burner status */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-black/30 border border-border/50 rounded p-2">
                      <span className="font-mono text-[9px] text-muted-foreground block">BOILER_TEMP</span>
                      <span className="font-mono text-xs font-bold text-white">{boilerTemp}°C</span>
                    </div>
                    <div className="bg-black/30 border border-border/50 rounded p-2">
                      <span className="font-mono text-[9px] text-muted-foreground block">BURNER_RATE</span>
                      <span className="font-mono text-xs font-bold text-emerald-400">85% STEAM</span>
                    </div>
                  </div>

                  {/* Valves checklist */}
                  <div>
                    <span className="font-mono text-[9px] text-muted-foreground uppercase tracking-widest block mb-2">VALVE_SYSTEM_MATRIX:</span>
                    <div className="grid grid-cols-3 gap-2">
                      {valves.map((val, idx) => (
                        <div key={idx} className="bg-black/40 border border-border/40 rounded p-1.5 flex flex-col items-center justify-center">
                          <span className="font-mono text-[8px] text-muted-foreground">V-{idx+1}</span>
                          <div className={`w-2 h-2 rounded-full mt-1 ${val ? 'bg-primary animate-pulse' : 'bg-red-500'}`} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mechanical lever trigger */}
                  <button 
                    onClick={handleVentSteam}
                    disabled={boilerVenting}
                    className={`w-full py-2.5 rounded border font-mono text-[10px] uppercase tracking-widest transition-all duration-200 ${
                      boilerVenting 
                        ? 'bg-amber-600/20 border-amber-500 text-amber-500 animate-pulse' 
                        : 'bg-transparent border-primary/40 text-primary hover:bg-primary/15 hover:border-primary'
                    }`}
                  >
                    {boilerVenting ? '💨 [ VENTING IN PROGRESS ]' : '⚙️ [ VENT EXCESS STEAM ]'}
                  </button>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section className="py-16 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-card via-card to-card border border-border cyber-card p-8 md:p-12">
            {/* Glows */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-secondary/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
            {/* Cyber grid overlay */}
            <div className="absolute inset-0 cyber-grid opacity-30 rounded-2xl" />

            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="max-w-xl">
                <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
                  <Radio className="w-3 h-3 text-secondary" />
                  <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">ENGINEER_ALLIANCE</span>
                </div>
                <h2 className="font-bold text-2xl md:text-3xl text-white uppercase mb-4 leading-tight">
                  {COPY.cta.titlePrefix}<span className="text-glow-green brand-gradient">{COPY.cta.titleHighlight}</span>{COPY.cta.titleSuffix}
                </h2>
                <p className="text-foreground/70 mb-6 text-sm leading-relaxed">
                  {COPY.cta.subtitle}
                </p>
                <Button
                  className="bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest py-6 px-8 neon-green transition-all duration-300"
                  onClick={() => window.location.href = '/reseller'}
                  data-testid="cta-reseller-btn"
                >
                  {COPY.cta.button}
                </Button>
              </div>

              {/* Compact visualizer */}
              <div className="hidden md:block flex-shrink-0">
                <HeroVisual compact />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
